import * as React from "react"
import { Pencil, ChevronLeft, ChevronRight, Settings, X, Minus, Trash2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { SortableHeader, type SortDirection } from "@/components/shared/sortable-header"
import { useToast } from "@/hooks/use-toast"
import { NoDevicesEmpty, NoSearchResultsEmpty, ApiErrorState } from "@/components/shared/empty-states"
import { DevicesTableSkeleton } from "@/components/shared/loading-skeletons"
import { DeviceEditPanel } from "@/components/gap-devices/device-edit-panel"
import { DeleteDeviceDialog } from "@/components/gap-devices/delete-device-dialog"
import { BulkUpdateDialog } from "@/components/gap-devices/bulk-update-dialog"
import type { SearchFiltersState } from "@/components/gap-devices/search-filters"
import type { GapDeviceUI, UpdateGapDeviceRequest } from "@/types/gap-device"
import type { GapClientConfig } from "@/types/gap-client"

type DeviceSortColumn =
  | "deviceId"
  | "clientId"
  | "areaCode"
  | "necMatchScoreThreshold"
  | "featureFlagUserInstructions"
  | "vendorName"
  | "vendorDeviceId"
  | "lastUpdated"

interface DevicesTableProps {
  devices: GapDeviceUI[]
  clients?: GapClientConfig[]
  isLoading?: boolean
  error?: Error | null
  onRetry?: () => void
  onAddDevice?: () => void
  highlightedDeviceId?: string | null
  filters?: SearchFiltersState
  onUpdateDevice: (deviceId: string, data: UpdateGapDeviceRequest) => Promise<void>
  onDeleteDevice: (deviceId: string) => Promise<void>
  onBulkDelete: (deviceIds: string[]) => Promise<void>
  onBulkUpdate: (updates: Array<{ deviceId: string; data: UpdateGapDeviceRequest }>) => Promise<void>
  isUpdating?: boolean
  isDeleting?: boolean
}

export function DevicesTable({
  devices,
  clients = [],
  isLoading,
  error,
  onRetry,
  onAddDevice,
  highlightedDeviceId,
  filters,
  onUpdateDevice,
  onDeleteDevice,
  onBulkDelete,
  onBulkUpdate,
  isUpdating = false,
  isDeleting = false,
}: DevicesTableProps) {
  const clientById = React.useMemo(() => {
    const map = new Map<string, GapClientConfig>()
    clients.forEach((c) => map.set(c.clientId, c))
    return map
  }, [clients])

  const [selectedDevices, setSelectedDevices] = React.useState<Set<string>>(new Set())
  const [editingDevice, setEditingDevice] = React.useState<GapDeviceUI | null>(null)
  const [isEditPanelOpen, setIsEditPanelOpen] = React.useState(false)
  const { toast } = useToast()

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [devicesToDelete, setDevicesToDelete] = React.useState<string[]>([])
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = React.useState(false)
  const [sortColumn, setSortColumn] = React.useState<DeviceSortColumn | null>(null)
  const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc")

  const handleSort = (column: DeviceSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortColumn(column)
      setSortDirection("asc")
    }
  }

  const filteredDevices = React.useMemo(() => {
    if (!filters) return devices

    return devices.filter((device) => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        const matchesSearch =
          device.deviceId.toLowerCase().includes(searchLower) ||
          device.clientId.toLowerCase().includes(searchLower) ||
          device.clientName.toLowerCase().includes(searchLower) ||
          device.vendorName.toLowerCase().includes(searchLower) ||
          device.vendorDeviceId.toLowerCase().includes(searchLower) ||
          device.areaCode?.toLowerCase()?.includes(searchLower)
        if (!matchesSearch) return false
      }

      if (filters.clients.size > 0) {
        if (!filters.clients.has(device.clientId)) return false
      }

      return true
    })
  }, [devices, filters])

  const sortedDevices = React.useMemo(() => {
    if (!sortColumn) return filteredDevices
    return [...filteredDevices].sort((a, b) => {
      let comparison = 0
      switch (sortColumn) {
        case "deviceId":
          comparison = a.deviceId.localeCompare(b.deviceId)
          break
        case "clientId":
          comparison = a.clientId.localeCompare(b.clientId)
          break
        case "areaCode":
          comparison = (a.areaCode ?? "").localeCompare(b.areaCode ?? "")
          break
        case "necMatchScoreThreshold": {
          const clientA = clientById.get(a.clientId)
          const clientB = clientById.get(b.clientId)
          const valA =
            a.necThresholdUseDefault && clientA != null
              ? clientA.necMatchScoreThreshold
              : parseInt(a.necThreshold, 10) ?? 0
          const valB =
            b.necThresholdUseDefault && clientB != null
              ? clientB.necMatchScoreThreshold
              : parseInt(b.necThreshold, 10) ?? 0
          comparison = valA - valB
          break
        }
        case "featureFlagUserInstructions": {
          const clientA = clientById.get(a.clientId)
          const clientB = clientById.get(b.clientId)
          const valA = a.userInstructionsUseDefault && clientA != null ? (clientA.featureFlagUserInstructions ? 1 : 0) : (a.userInstructionsEnabled ? 1 : 0)
          const valB = b.userInstructionsUseDefault && clientB != null ? (clientB.featureFlagUserInstructions ? 1 : 0) : (b.userInstructionsEnabled ? 1 : 0)
          comparison = valA - valB
          break
        }
        case "vendorName":
          comparison = a.vendorName.localeCompare(b.vendorName)
          break
        case "vendorDeviceId":
          comparison = a.vendorDeviceId.localeCompare(b.vendorDeviceId)
          break
        case "lastUpdated":
          comparison =
            new Date(a.lastUpdatedRaw).getTime() - new Date(b.lastUpdatedRaw).getTime()
          break
      }
      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [filteredDevices, sortColumn, sortDirection, clientById])

  const totalDevices = devices.length
  const displayedDevices = filteredDevices.length

  const toggleDevice = (deviceId: string) => {
    setSelectedDevices((prev) => {
      const newSelection = new Set(prev)
      if (newSelection.has(deviceId)) {
        newSelection.delete(deviceId)
      } else {
        newSelection.add(deviceId)
      }
      return newSelection
    })
  }

  const toggleAll = () => {
    if (selectedDevices.size === filteredDevices.length && filteredDevices.length > 0) {
      setSelectedDevices(new Set())
    } else {
      setSelectedDevices(new Set(filteredDevices.map((d) => d.deviceId)))
    }
  }

  const clearSelection = () => {
    setSelectedDevices(new Set())
  }

  const handleBulkSave = async () => {
    if (selectedDevices.size === 0) return
    setBulkUpdateDialogOpen(true)
  }

  const handleConfirmBulkUpdate = async (partialData: UpdateGapDeviceRequest) => {
    try {
      // Build full update payload per device (backend requires full object).
      // Always explicitly set overridable fields so the API only overrides when useDefault is true for that field.
      const updates = Array.from(selectedDevices).map((deviceId) => {
        const device = devices.find((d) => d.deviceId === deviceId)
        const fullData: UpdateGapDeviceRequest = {
          clientId: device?.clientId,
          vendorDeviceId: device?.vendorDeviceId,
          vendorName: device?.vendorName,
          areaCode: device?.areaCode,
          ...partialData,
          necMatchScoreThreshold:
            partialData.necMatchScoreThreshold ??
            (device
              ? {
                  useDefault: device.necThresholdUseDefault,
                  ...(device.necThresholdUseDefault ? {} : { value: parseInt(device.necThreshold, 10) ?? 0 }),
                }
              : undefined),
          featureFlagUserInstructions:
            partialData.featureFlagUserInstructions ??
            (device
              ? {
                  useDefault: device.userInstructionsUseDefault,
                  ...(device.userInstructionsUseDefault ? {} : { value: device.userInstructionsEnabled }),
                }
              : undefined),
        }
        return { deviceId, data: fullData }
      })
      await onBulkUpdate(updates)
      setBulkUpdateDialogOpen(false)
      // Toast notification in the parent component (devices.tsx) to show accurate success/failure counts
      setSelectedDevices(new Set())
    } catch {
      // Error toast handled by parent component
    }
  }

  const handleDeleteClick = (deviceId: string) => {
    setDevicesToDelete([deviceId])
    setDeleteDialogOpen(true)
  }

  const handleBulkDeleteClick = () => {
    setDevicesToDelete(Array.from(selectedDevices))
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (devicesToDelete.length === 1) {
      await onDeleteDevice(devicesToDelete[0])
    } else {
      await onBulkDelete(devicesToDelete)
    }

    setDeleteDialogOpen(false)
    setSelectedDevices((prev) => {
      const newSelection = new Set(prev)
      devicesToDelete.forEach((id) => newSelection.delete(id))
      return newSelection
    })

    toast({
      variant: "success",
      title: devicesToDelete.length > 1 ? `${devicesToDelete.length} devices deleted` : "Device deleted",
      description:
        devicesToDelete.length > 1
          ? "The selected devices have been permanently removed."
          : `${devicesToDelete[0]} has been permanently removed.`,
    })

    setDevicesToDelete([])
  }

  const handleEditDevice = (device: GapDeviceUI) => {
    setEditingDevice(device)
    setIsEditPanelOpen(true)
  }

  const handleSaveDevice = async (deviceId: string, data: UpdateGapDeviceRequest) => {
    await onUpdateDevice(deviceId, data)
    toast({
      variant: "success",
      title: "Device updated successfully",
      description: `${deviceId} has been saved.`,
    })
  }

  const isAllSelected = selectedDevices.size === filteredDevices.length && filteredDevices.length > 0
  const isIndeterminate = selectedDevices.size > 0 && selectedDevices.size < filteredDevices.length
  const hasSelection = selectedDevices.size > 0

  if (isLoading) {
    return <DevicesTableSkeleton />
  }

  if (error) {
    return <ApiErrorState onRetry={onRetry} />
  }

  if (devices.length === 0) {
    return <NoDevicesEmpty onAction={onAddDevice} />
  }

  if (
    filteredDevices.length === 0 &&
    filters &&
    (filters.search || filters.clients.size > 0)
  ) {
    return <NoSearchResultsEmpty onAction={() => {}} />
  }

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          {/* Bulk Actions Toolbar */}
          <div
            className={`grid transition-all duration-150 ease-out ${
              hasSelection ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
          >
            <div className="overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-primary">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium bg-primary-foreground/20 text-primary-foreground">
                    {selectedDevices.size} device{selectedDevices.size !== 1 ? "s" : ""} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleBulkSave}
                      className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30 hover:text-primary-foreground h-8"
                    >
                      <Settings className="h-4 w-4 mr-1.5" />
                      Edit Configuration
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleBulkDeleteClick}
                      className="bg-destructive/30 text-primary-foreground hover:bg-destructive/50 hover:text-primary-foreground h-8"
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" />
                      Delete
                    </Button>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearSelection}
                  className="text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/20 h-8"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear selection
                </Button>
              </div>
            </div>
          </div>

          <div className="table-scroll-x overflow-x-auto">
            <Table className="min-w-max w-full">
              <TableHeader>
                <TableRow className="border-border hover:bg-card">
                  <TableHead className="w-12">
                    <div className="relative">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleAll}
                        className="border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      {isIndeterminate && (
                        <div
                          className="absolute inset-0 flex items-center justify-center pointer-events-none"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="w-4 h-4 rounded-[4px] bg-primary border border-primary flex items-center justify-center">
                            <Minus className="h-3 w-3 text-primary-foreground" />
                          </div>
                        </div>
                      )}
                    </div>
                  </TableHead>
                  <SortableHeader
                    label="Device ID"
                    sortKey="deviceId"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Client ID"
                    sortKey="clientId"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Area Code"
                    sortKey="areaCode"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="NEC Threshold"
                    sortKey="necMatchScoreThreshold"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="User Instructions"
                    sortKey="featureFlagUserInstructions"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Vendor Name"
                    sortKey="vendorName"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Vendor Device ID"
                    sortKey="vendorDeviceId"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Last Updated"
                    sortKey="lastUpdated"
                    currentSortColumn={sortColumn}
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <TableHead className="text-muted-foreground font-medium w-16 sticky right-0 bg-card z-10 min-w-16">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedDevices.map((device, index) => {
                  const isSelected = selectedDevices.has(device.deviceId)
                  const isHighlighted = highlightedDeviceId === device.deviceId
                  return (
                    <TableRow
                      key={device.deviceId}
                      className={`border-border transition-colors group ${
                        isHighlighted
                          ? "bg-success/20 animate-pulse"
                          : isSelected
                            ? "bg-primary/10 hover:bg-primary/15"
                            : index % 2 === 1
                              ? "bg-muted/30 hover:bg-muted/50"
                              : "hover:bg-muted/50"
                      }`}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleDevice(device.deviceId)}
                          className="border-input hover:border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{device.deviceId}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{device.clientId}</div>
                      </TableCell>
                      <TableCell>
                        {device.areaCode ? (
                          <div className="font-medium text-foreground">{device.areaCode}</div>
                        ) : (
                          <span className="font-medium text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const client = clientById.get(device.clientId)
                          const displayThreshold = device.necThresholdUseDefault && client != null
                            ? String(client.necMatchScoreThreshold)
                            : device.necThreshold
                          return (
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-foreground">{displayThreshold}</span>
                              {device.necThresholdUseDefault && (
                                <span className="text-xs text-muted-foreground" title="Uses default from client">
                                  (default)
                                </span>
                              )}
                            </div>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const client = clientById.get(device.clientId)
                          const displayEnabled = device.userInstructionsUseDefault && client != null
                            ? client.featureFlagUserInstructions
                            : device.userInstructionsEnabled
                          return (
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                  displayEnabled ? "bg-success/20 text-success" : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {displayEnabled ? "Enabled" : "Disabled"}
                              </span>
                              {device.userInstructionsUseDefault && (
                                <span className="text-xs text-muted-foreground" title="Uses default from client">
                                  (default)
                                </span>
                              )}
                            </div>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{device.vendorName}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{device.vendorDeviceId}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-foreground">{device.lastUpdated}</div>
                      </TableCell>
                      <TableCell className="sticky right-0 bg-card z-10 min-w-16">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditDevice(device)}
                            className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 w-8"
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Edit device</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(device.deviceId)}
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete device</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          {/* Footer with pagination info */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {displayedDevices < totalDevices
                ? `Showing ${displayedDevices} of ${totalDevices} devices (filtered)`
                : `Showing ${totalDevices} device${totalDevices !== 1 ? "s" : ""}`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled
                className="bg-card border-border text-muted-foreground hover:bg-secondary"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled
                className="bg-card border-border text-muted-foreground hover:bg-secondary"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Panel */}
      <DeviceEditPanel
        device={editingDevice}
        open={isEditPanelOpen}
        onOpenChange={setIsEditPanelOpen}
        onSave={handleSaveDevice}
        isSaving={isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDeviceDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        deviceIds={devicesToDelete}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />

      {/* Bulk Update Dialog */}
      <BulkUpdateDialog
        open={bulkUpdateDialogOpen}
        onOpenChange={setBulkUpdateDialogOpen}
        deviceIds={Array.from(selectedDevices)}
        onConfirm={handleConfirmBulkUpdate}
        isLoading={isUpdating}
      />
    </div>
  )
}
